import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Search, X, Users, UserPlus } from 'lucide-react';
import { communityService, Conversation, ConnectionUser } from '../../services/communityService';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';
import PageHeader from '../../components/PageHeader';

const CONVERSATIONS_POLL_INTERVAL = 5000; // 5 seconds
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export default function ConversationsListPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [friends, setFriends] = useState<ConnectionUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadInitial();

    // Poll for new conversations/messages every 5 seconds
    pollingRef.current = setInterval(async () => {
      try {
        const data = await communityService.getConversations();
        setConversations(data);
      } catch {
        // silently ignore polling errors
      }
    }, CONVERSATIONS_POLL_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const loadInitial = async () => {
    try {
      setLoading(true);
      const [convs, frs] = await Promise.all([
        communityService.getConversations().catch(() => []),
        communityService.getMyFriends().catch(() => []),
      ]);
      setConversations(convs);
      setFriends(frs);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la încărcarea conversațiilor');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ro });
    } catch {
      return 'recent';
    }
  };

  const getPhotoUrl = (photoUrl?: string | null) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http') || photoUrl.startsWith('data:')) return photoUrl;
    return `${API_URL}${photoUrl}`;
  };

  // Apply client-side search filter on conversations
  const filteredConversations = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const name = `${c.otherUser.firstName || ''} ${c.otherUser.lastName || ''}`.toLowerCase();
      const lastMsg = c.lastMessage?.content?.toLowerCase() || '';
      return name.includes(q) || lastMsg.includes(q);
    });
  }, [conversations, query]);

  // Friends you haven't messaged yet (quick-start suggestions)
  const friendsNotInConversations = useMemo(() => {
    const convUserIds = new Set(conversations.map((c) => c.otherUser.id));
    return friends.filter((f) => !convUserIds.has(f.id));
  }, [friends, conversations]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Se încarcă conversațiile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader
        title="Mesaje Private"
        showBack={true}
        backTo="/community"
        actions={
          <button
            onClick={() => navigate('/community/friends')}
            className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Prietenii mei"
          >
            <Users className="w-5 h-5" />
            <span className="hidden sm:inline">Prieteni</span>
          </button>
        }
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Mesajele tale</h1>
          <p className="text-sm text-gray-600">Conversații private cu alți membri</p>
        </div>

        {/* Search bar */}
        {conversations.length > 0 && (
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Caută după nume sau mesaj..."
                className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  aria-label="Golește"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Friends quick-start (horizontal scroll) */}
        {!query && friendsNotInConversations.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Users className="w-4 h-4 text-blue-600" />
                Prietenii tăi
              </h2>
              <button
                onClick={() => navigate('/community/friends')}
                className="text-xs text-blue-600 hover:underline"
              >
                Vezi toți ({friends.length})
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto py-2 -mx-2 px-2 scrollbar-none">
              {friendsNotInConversations.slice(0, 12).map((f) => {
                const photo = getPhotoUrl(f.profilePhotoUrl);
                return (
                  <button
                    key={f.id}
                    onClick={() => navigate(`/community/messages/${f.id}`)}
                    className="flex flex-col items-center flex-shrink-0 w-16 group"
                    title={`Trimite mesaj lui ${f.firstName}`}
                  >
                    <div className="relative">
                      {photo ? (
                        <img
                          src={photo}
                          alt={`${f.firstName}`}
                          className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm group-hover:ring-2 group-hover:ring-blue-400 transition"
                        />
                      ) : (
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold shadow-sm group-hover:ring-2 group-hover:ring-blue-400 transition">
                          {(f.firstName?.[0] || 'U').toUpperCase()}
                        </div>
                      )}
                      <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 rounded-full border-2 border-white flex items-center justify-center">
                        <MessageCircle className="w-2.5 h-2.5 text-white" />
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 mt-1 text-center truncate w-full">
                      {f.firstName}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Conversations list */}
        {filteredConversations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200 overflow-hidden">
            {filteredConversations.map((conversation) => {
              const photo = getPhotoUrl(conversation.otherUser.profilePhotoUrl);
              return (
                <div
                  key={conversation.conversationId}
                  onClick={() => navigate(`/community/messages/${conversation.otherUser.id}`)}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    conversation.unreadCount > 0 ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {photo ? (
                        <img
                          src={photo}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {conversation.otherUser.firstName?.[0]?.toUpperCase() || 'U'}
                        </div>
                      )}
                      {conversation.unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold px-1">
                          {conversation.unreadCount}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {conversation.otherUser.firstName} {conversation.otherUser.lastName}
                        </h3>
                        {conversation.lastMessage && (
                          <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                            {formatTimeAgo(conversation.lastMessage.createdAt)}
                          </span>
                        )}
                      </div>

                      {conversation.lastMessage ? (
                        <p
                          className={`text-sm truncate ${
                            conversation.unreadCount > 0
                              ? 'font-medium text-gray-900'
                              : 'text-gray-600'
                          }`}
                        >
                          {conversation.lastMessage.isSentByMe && (
                            <span className="text-gray-400 mr-1">Tu:</span>
                          )}
                          {conversation.lastMessage.content}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">Începe o conversație</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state — no search results */}
        {query && filteredConversations.length === 0 && (
          <div className="text-center py-10 bg-white rounded-lg border border-gray-200">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 mb-3">Niciun rezultat pentru „{query}"</p>
            <button onClick={() => setQuery('')} className="text-sm text-blue-600 hover:underline">
              Golește căutarea
            </button>
          </div>
        )}

        {/* Empty state — no conversations at all */}
        {!query && conversations.length === 0 && !loading && !error && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nu ai conversații încă</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Adaugă prieteni din comunitate și începe o conversație privată oricând dorești.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => navigate('/community')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Explorează comunitatea
              </button>
              <button
                onClick={() => navigate('/community/friends')}
                className="bg-white text-blue-600 border border-blue-200 px-6 py-2 rounded-lg hover:bg-blue-50 transition-colors inline-flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Prietenii mei
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
